import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Utensils, Flame, Compass } from "lucide-react";
import { MENU, TASTING } from "@/data/menu";
import { Reveal, LineReveal } from "@/components/motion";

const NAV = [
  ...MENU.map((c) => ({ id: c.id, title: c.title })),
  { id: "tasting", title: "Tasting Menus" },
];

function DishRow({ d }) {
  return (
    <div
      className="group relative p-4 md:p-6 rounded-xl border border-transparent hover:border-gold/25 hover:bg-cream/[0.03] transition-all duration-300 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-b border-cream/10"
      data-testid="menu-dish"
    >
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h4 className="font-display text-2xl md:text-3xl text-cream group-hover:text-gold transition-colors duration-300">
            {d.name}
          </h4>
          {d.featured && (
            <span className="inline-flex items-center gap-1 font-body text-[10px] uppercase tracking-widest px-2.5 py-0.5 rounded-full border border-gold/40 bg-gold/10 text-gold font-medium">
              <Sparkles size={10} /> Signature
            </span>
          )}
        </div>
        <p className="font-body font-light text-cream/65 text-sm md:text-base max-w-2xl leading-relaxed">
          {d.desc}
        </p>
      </div>

      {d.image && (
        <div className="w-full md:w-32 h-40 md:h-32 rounded-lg overflow-hidden shrink-0 border border-gold/30 shadow-lg relative group-hover:border-gold transition-all duration-300">
          <img
            src={d.image}
            alt={d.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent pointer-events-none" />
        </div>
      )}
    </div>
  );
}

function Category({ cat }) {
  return (
    <section id={cat.id} className="scroll-mt-32 mb-16 md:mb-20" data-testid={`menu-category-${cat.id}`}>
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6 border-b border-gold/40 pb-4">
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-gold" />
            <h3 className="font-display text-3xl md:text-5xl text-cream tracking-wide">{cat.title}</h3>
          </div>
          <span className="font-script text-gold text-lg md:text-xl shrink-0">Chef Jomon's #VEEDU</span>
        </div>
        {cat.subtitle && (
          <p className="font-display italic text-xl md:text-2xl text-gold/90 mb-6 pl-11 border-l border-gold/30">
            {cat.subtitle}
          </p>
        )}
      </Reveal>

      <div className="space-y-1">
        {cat.dishes && cat.dishes.map((d) => <DishRow key={d.name} d={d} />)}
      </div>

      {cat.groups &&
        cat.groups.map((g) => (
          <div key={g.name} className="mt-8 mb-8">
            <div className="mb-4 pl-3 border-l-2 border-terracotta">
              <h4 className="font-display text-2xl md:text-3xl text-gold">{g.name}</h4>
              <p className="u-eyebrow text-terracotta mt-1 text-xs">{g.note}</p>
            </div>
            <div className="space-y-1">
              {g.dishes.map((d) => <DishRow key={d.name} d={d} />)}
            </div>
          </div>
        ))}
    </section>
  );
}

function TastingSection() {
  return (
    <section id="tasting" className="scroll-mt-32 mb-12" data-testid="menu-category-tasting">
      <Reveal>
        <div className="flex items-end justify-between mb-8 border-b border-gold/40 pb-4">
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-gold" />
            <h3 className="font-display text-3xl md:text-5xl text-cream">Tasting Menus</h3>
          </div>
          <span className="u-eyebrow text-gold">Curated Journeys</span>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {TASTING.map((t) => (
          <Reveal key={t.title}>
            <div
              className="bg-gradient-to-b from-brown/90 to-charcoal text-cream p-6 md:p-10 h-full border border-gold/30 rounded-xl shadow-2xl relative overflow-hidden"
              data-testid="tasting-card"
            >
              <div className="flex items-center justify-between gap-4 mb-2">
                <span className="u-eyebrow text-gold">{t.title}</span>
                <span className="px-3 py-1 rounded-full border border-gold/40 text-gold text-xs font-body uppercase tracking-wider bg-gold/10">
                  Chef Special
                </span>
              </div>
              <h4 className="font-display text-3xl md:text-4xl mb-6 text-cream">{t.subtitle}</h4>
              
              <ol className="space-y-4">
                {t.courses.map((c, i) => (
                  <li key={c.name} className="flex gap-4 border-b border-cream/10 pb-4">
                    <span className="w-7 h-7 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0 text-gold font-display italic text-sm">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-display text-lg md:text-xl text-cream">{c.name}</p>
                      <p className="font-body font-light text-cream/65 text-xs md:text-sm mt-1 leading-relaxed">{c.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>

              {t.note && <p className="font-script text-gold text-xl mt-6 italic">{t.note}</p>}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Menu() {
  const [active, setActive] = useState(NAV[0].id);
  const ribbonRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    NAV.forEach((n) => {
      const el = document.getElementById(n.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -110 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-ink min-h-screen" data-testid="page-menu">
      {/* Header section with minimal padding & tight line height */}
      <section className="pt-24 md:pt-28 pb-8 u-container relative" data-testid="menu-header">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-px bg-gold" />
            <span className="u-eyebrow text-terracotta">The Culinary Journey</span>
          </div>
        </Reveal>
        <LineReveal
          trigger="load"
          lines={["Every dish has somewhere to take you."]}
          lineClassName="font-display text-4xl md:text-6xl lg:text-7xl leading-tight text-cream"
        />
        <Reveal delay={0.2}>
          <p className="font-body font-light text-cream/65 text-base md:text-lg max-w-2xl mt-4 leading-relaxed">
            The menu is intentionally diverse — rooted in Kerala coastlines, tandoor heat, and family memories. Read it slowly.
          </p>
        </Reveal>

        {/* Feature Pills */}
        <Reveal delay={0.3}>
          <div className="flex flex-wrap items-center gap-3 mt-6 pt-6 border-t border-cream/10">
            <span className="inline-flex items-center gap-1.5 font-body text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-gold/30 text-gold/90 bg-gold/5">
              <Utensils size={12} /> Coastal Cuisine
            </span>
            <span className="inline-flex items-center gap-1.5 font-body text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-gold/30 text-gold/90 bg-gold/5">
              <Flame size={12} /> Tandoor Fire
            </span>
            <span className="inline-flex items-center gap-1.5 font-body text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-gold/30 text-gold/90 bg-gold/5">
              <Compass size={12} /> Heritage Recipes
            </span>
            <span className="inline-flex items-center gap-1.5 font-body text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-gold/30 text-gold/90 bg-gold/5">
              <Sparkles size={12} /> Tasting Menus
            </span>
          </div>
        </Reveal>
      </section>

      {/* Mobile Sticky Navigation Ribbon */}
      <div
        ref={ribbonRef}
        className="lg:hidden sticky top-[76px] z-30 bg-ink/95 backdrop-blur-md border-y border-gold/20 no-scrollbar overflow-x-auto"
        data-testid="menu-ribbon"
      >
        <div className="flex gap-4 px-6 py-3 whitespace-nowrap">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => goTo(n.id)}
              data-testid={`menu-nav-mobile-${n.id}`}
              className={`u-eyebrow px-3 py-1 rounded-full transition-all text-xs ${
                active === n.id
                  ? "text-gold border border-gold/50 bg-gold/10"
                  : "text-cream/50 hover:text-cream"
              }`}
            >
              {n.title}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="u-container grid grid-cols-1 lg:grid-cols-12 gap-10 pb-20 pt-8">
        {/* Desktop Sidebar Navigation */}
        <aside className="hidden lg:block lg:col-span-3">
          <nav className="sticky top-28 space-y-1 bg-charcoal/40 p-5 rounded-xl border border-gold/15" data-testid="menu-nav-desktop">
            <p className="u-eyebrow text-gold mb-3 px-2 text-[10px]">Menu Navigation</p>
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => goTo(n.id)}
                data-testid={`menu-nav-${n.id}`}
                className={`group flex items-center gap-3 w-full text-left py-2 px-2.5 rounded-lg transition-all text-xs uppercase tracking-wider ${
                  active === n.id
                    ? "text-gold font-medium bg-gold/10 border-l-2 border-gold"
                    : "text-cream/50 hover:text-cream hover:bg-cream/5"
                }`}
              >
                <span>{n.title}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Categories Content */}
        <div className="lg:col-span-9">
          {MENU.map((cat) => <Category key={cat.id} cat={cat} />)}
          <TastingSection />

          {/* Bottom Banner */}
          <div className="mt-12 border border-gold/30 bg-gradient-to-r from-brown/80 to-charcoal p-8 md:p-10 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <span className="u-eyebrow text-gold mb-2 block">Join Us at #VEEDU</span>
              <p className="font-display italic text-2xl md:text-3xl text-cream max-w-md">
                Come and listen to the food tales of Kerala.
              </p>
            </div>
            <Link
              to="/contact#enquire"
              data-testid="menu-cta-contact"
              className="btn-veedu border-gold text-gold hover:bg-gold hover:text-ink transition-colors px-6 py-3 shrink-0"
            >
              Reserve Table <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
